import {
  AiInterviewerFragment,
  AiInterviewerRolePermission,
  CurrentUserFragment,
  UserRoleName,
} from "../graphql";
import { useFeatureFlagForCurrentOrImpersonatedUser } from "../graphql/hooks/useFeatureFlag";
import { useOptionalCurrentUser } from "./useCurrentUser";
import { useIsBHScreenRbacEnabled } from "./useIsBHScreenRbacEnabled";

interface BHScreenPermissionResult {
  canView: boolean;
  canEdit: boolean;
  canManage: boolean;
}

// Minimal type for permission checking - both AiInterviewerFragment and
// AiInterviewerListItemFragment satisfy this after adding currentUserCan* fields
type AiInterviewerWithPermissions = Pick<
  AiInterviewerFragment,
  "currentUserCanView" | "currentUserCanEdit" | "currentUserCanManage"
>;

export const REQUIRES_BH_SCREEN_MANAGE_PERMISSION =
  "Requires permission to Manage AI Interviewers";

const getBHScreenPermissionLegacy = (
  currentUser: CurrentUserFragment | null,
  nonAdminAccessEnabled: boolean
): BHScreenPermissionResult => {
  const isSiteAdmin = currentUser?.userRole?.name === UserRoleName.SiteAdmin;
  const allowed = !!isSiteAdmin || nonAdminAccessEnabled;

  return {
    canView: allowed,
    canEdit: allowed,
    canManage: allowed,
  };
};

const getBHScreenPermissionWithRBAC = (
  aiInterviewer: AiInterviewerWithPermissions | undefined,
  currentUser: CurrentUserFragment | null
): BHScreenPermissionResult => {
  // If we have a specific interviewer, use backend-provided permission flags
  if (aiInterviewer) {
    return {
      canView: aiInterviewer.currentUserCanView ?? false,
      canEdit: aiInterviewer.currentUserCanEdit ?? false,
      canManage: aiInterviewer.currentUserCanManage ?? false,
    };
  }

  // FIXED: Check user's Screen Role permissions for global access
  const screenRole = currentUser?.aiInterviewerUserRole;
  if (screenRole?.permissions) {
    const { permissions } = screenRole;
    return {
      canView: permissions.includes(AiInterviewerRolePermission.View),
      canEdit: permissions.includes(AiInterviewerRolePermission.Edit),
      canManage: permissions.includes(AiInterviewerRolePermission.Manage),
    };
  }

  // No Screen Role = no global access in RBAC mode
  // Note: Share-only users (no role but has memberships) correctly get false here
  // because they only have per-interviewer access, not global access.
  // The useAiInterviewerEnabled hook handles them via `|| hasShares`.
  return { canView: false, canEdit: false, canManage: false };
};

export const useBHScreenPermission = (
  aiInterviewer?: AiInterviewerWithPermissions
): BHScreenPermissionResult => {
  const currentUser = useOptionalCurrentUser();
  const nonAdminAccessEnabled = useFeatureFlagForCurrentOrImpersonatedUser(
    "ai_interviewer:non-admin-user-access"
  );
  const isRbacEnabled = useIsBHScreenRbacEnabled();

  if (isRbacEnabled) {
    return getBHScreenPermissionWithRBAC(aiInterviewer, currentUser);
  }

  return getBHScreenPermissionLegacy(currentUser, nonAdminAccessEnabled);
};
